import { AtSign, FileText, Headset, NotebookPen } from 'lucide-react';
import type { DesignStep } from '@/components/ai-elements/document-card';
import type {
  AIGenerationMode,
  DocumentCreationStatus,
  DocumentCreationStep,
  FirstMessageState,
  SourceOption,
  SourceType,
  StagingModelOption,
  ThemeSelectionResponse,
} from './types';

export const DEFAULT_AI_MODE: AIGenerationMode = 'guided';

export const AI_MODE_OPTIONS: Array<{
  value: AIGenerationMode;
  label: string;
  description: string;
}> = [
  {
    value: 'guided',
    label: 'Guided',
    description: 'We’ll ask a few questions first, then start drafting.',
  },
  {
    value: 'draft_ready',
    label: 'Express',
    description: 'Create an editable draft without questions.',
  },
  {
    value: 'collaborative',
    label: 'Collaborative',
    description: 'We’ll work section by section with you.',
  },
];

export const SOURCE_OPTIONS: SourceOption[] = [
  {
    id: 'transcript',
    label: 'Call transcript',
    icon: Headset,
    modalTitle: 'Add call transcript',
    modalDescription: "Paste your call notes. We'll extract requirements automatically.",
    placeholder: '5 Webflow pages, 2 rounds of revision, delivery in 4 weeks, $5,500 total',
    submitLabel: 'Add transcript',
  },
  {
    id: 'rfp',
    label: 'RFP/RFQ',
    icon: FileText,
    modalTitle: 'Upload RFP/RFQ',
    modalDescription: 'Upload your RFP or RFQ document so AI can structure the response.',
    placeholder: '',
    submitLabel: 'Add RFP/RFQ',
  },
  {
    id: 'email',
    label: 'Email content',
    icon: AtSign,
    modalTitle: 'Add email content',
    modalDescription: 'Paste email threads with client context and expectations.',
    placeholder: '5 Webflow pages, 2 rounds of revision, delivery in 4 weeks, $5,500 total',
    submitLabel: 'Add email content',
  },
  {
    id: 'meeting',
    label: 'Meeting notes',
    icon: NotebookPen,
    modalTitle: 'Add meeting notes',
    modalDescription: 'Paste structured notes from internal or external meetings.',
    placeholder: '5 Webflow pages, 2 rounds of revision, delivery in 4 weeks, $5,500 total',
    submitLabel: 'Add meeting notes',
  },
];

export const sourceToOptionMap = SOURCE_OPTIONS.reduce<Record<SourceType, SourceOption>>(
  (acc, option) => {
    acc[option.id] = option;
    return acc;
  },
  {} as Record<SourceType, SourceOption>,
);

export const SOURCE_FILE_ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/jpeg',
  'image/png',
];

export const SOURCE_FILE_ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.jpg',
  '.jpeg',
  '.png',
];

export const SOURCE_FILE_ACCEPT_STRING = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png';

// Allowed file extensions for document agent uploads (matches Laravel api/upload endpoint)
export const DOCUMENT_AGENT_ALLOWED_EXTENSIONS = [
  'bmp',
  'jpeg',
  'jpg',
  'png',
  'tiff',
  'gif',
  'webp',
  'svg',
  'pdf',
  'doc',
  'docx',
  'odt',
  'rtf',
  'xls',
  'xlsx',
  'ods',
  'ppt',
  'pptx',
  'odp',
  'txt',
  'md',
  'csv',
  'html',
  'xml',
  'json',
] as const;

export const DOCUMENT_AGENT_ALLOWED_MIME_TYPES = [
  'image/bmp',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.oasis.opendocument.text',
  'text/rtf',
  'application/rtf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.presentation',
  'text/plain',
  'text/markdown',
  'text/csv',
  'text/html',
  'text/xml',
  'application/xml',
  'application/json',
] as const;

export const DOCUMENT_AGENT_ACCEPT_STRING = DOCUMENT_AGENT_ALLOWED_EXTENSIONS.map(
  (ext) => `.${ext}`,
).join(',');

export const isAllowedFileExtension = (filename: string): boolean => {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext
    ? DOCUMENT_AGENT_ALLOWED_EXTENSIONS.includes(
        ext as (typeof DOCUMENT_AGENT_ALLOWED_EXTENSIONS)[number],
      )
    : false;
};

export const isAllowedMimeType = (mimeType: string): boolean => {
  return DOCUMENT_AGENT_ALLOWED_MIME_TYPES.includes(
    mimeType as (typeof DOCUMENT_AGENT_ALLOWED_MIME_TYPES)[number],
  );
};

export const MICROPHONE_RECORDING_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/wav',
];

// UI hard-caps recordings at 5 minutes, but we still keep a byte guard so less efficient
// browser encodings cannot exceed the server's 15 MB transcription limit.
export const MAX_RECORDING_BYTES = 14 * 1024 * 1024;
export const MAX_RECORDING_SECONDS = 300;
export const RECORDING_WARNING_SECONDS = 30;
export const TRANSCRIPTION_REQUEST_TIMEOUT_MS = 120_000;
export const TRANSCRIPTION_MAX_ATTEMPTS = 3;

export const DOCUMENT_AGENT_MAX_FILES_PER_REQUEST = 10;
export const DOCUMENT_AGENT_MAX_TOTAL_BYTES_PER_REQUEST = 50 * 1024 * 1024;

// Static registry of content block display info (from master-schema-registry.json)
export const CONTENT_BLOCK_REGISTRY: Record<
  string,
  { name: string; description: string; keyFields: string[] }
> = {
  services: {
    name: 'Services',
    description: 'Business services and offerings',
    keyFields: ['name', 'description'],
  },
  portfolio: {
    name: 'Portfolio',
    description: 'Portfolio projects and work samples',
    keyFields: ['title', 'project_type'],
  },
  case_studies: {
    name: 'Case Studies',
    description: 'Detailed case studies and success stories',
    keyFields: ['case_study_title', 'challenge', 'solution'],
  },
  team: {
    name: 'Team',
    description: 'Team members and their profiles',
    keyFields: ['name', 'role'],
  },
  pricing_packages: {
    name: 'Pricing & Packages',
    description: 'Pricing packages and plans',
    keyFields: ['package_name', 'price', 'features'],
  },
  technologies: {
    name: 'Technologies',
    description: 'Technology stack and tools',
    keyFields: ['technology_name'],
  },
  awards_recognition: {
    name: 'Awards & Recognition',
    description: 'Awards and industry recognition',
    keyFields: ['award_name', 'issuer', 'year'],
  },
  testimonials: {
    name: 'Testimonials',
    description: 'Client testimonials and reviews',
    keyFields: ['clientName', 'clientCompany', 'answer'],
  },
  about_us: {
    name: 'About Us',
    description: 'Company background and overview',
    keyFields: ['description'],
  },
  why_work_with_us: {
    name: 'Why Work with Us',
    description: 'Company value proposition',
    keyFields: ['title', 'description'],
  },
  projects: {
    name: 'Projects',
    description: 'Current and past projects',
    keyFields: ['project_name', 'objective'],
  },
  products: {
    name: 'Products',
    description: 'Products offered by the business',
    keyFields: ['product_name', 'description'],
  },
  faqs: {
    name: 'FAQs',
    description: 'Frequently asked questions',
    keyFields: ['question', 'answer', 'category'],
  },
  terms: {
    name: 'Terms',
    description: 'Business terms and policies',
    keyFields: ['payment_terms', 'cancellation_policy'],
  },
  legal_compliance: {
    name: 'Legal Compliance',
    description: 'Legal and compliance information',
    keyFields: ['certification', 'compliance_standards', 'data_residency'],
  },
  industries: {
    name: 'Industries',
    description: 'Industry focus and target markets',
    keyFields: ['industry', 'geographic_focus', 'target_company_size'],
  },
  methodology: {
    name: 'Methodology',
    description: 'Business processes and methodologies',
    keyFields: ['phase', 'steps', 'deliverables'],
  },
  key_metrics: {
    name: 'Key Metrics',
    description: 'Key performance metrics and statistics',
    keyFields: ['metric_name', 'baseline_value', 'improved_value'],
  },
  social_links: {
    name: 'Social Links',
    description: 'Organization social profile links',
    keyFields: ['platform', 'label', 'url'],
  },
};

export const DOCUMENT_AGENT_REASONING_STORAGE_KEY = 'document-agent:reasoning-effort:v1';
export const DOCUMENT_AGENT_SIDEBAR_STORAGE_KEY = 'document-agent:sidebar-open:v1';
// Chat ids whose brand search card was skipped.
export const DOCUMENT_AGENT_BRAND_SEARCH_SKIPPED_STORAGE_KEY =
  'document-agent:brand-search-skipped:v1';
export const DOCUMENT_AGENT_ROUTE_MODE_STORAGE_KEY = 'document-agent:route:document-agent:v1';

export const STAGING_REASONING_EFFORT_OPTIONS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
] as const;

export type StagingReasoningEffort = (typeof STAGING_REASONING_EFFORT_OPTIONS)[number]['id'];
export const DEFAULT_STAGING_REASONING_EFFORT: StagingReasoningEffort = 'medium';

export const isStagingReasoningEffort = (value: string): value is StagingReasoningEffort =>
  STAGING_REASONING_EFFORT_OPTIONS.some((option) => option.id === value);

// Curated from OpenRouter catalog for staging testing.
// DeepSeek V4.1 Flash is the default for new chats to keep dev spend low; the premium models are opt-in.
export const DEFAULT_STAGING_MODEL_ID = 'deepseek/deepseek-v4.1-flash';

const SHARED_STAGING_OPENROUTER_MODEL_OPTIONS: StagingModelOption[] = [
  { id: 'deepseek/deepseek-v4.1-flash', label: 'DeepSeek · V4.1 Flash' },
  { id: 'openai/gpt-6-luna', label: 'OpenAI · GPT-6 Luna' },
  { id: 'openai/gpt-5.4', label: 'OpenAI · GPT-5.4' },
  { id: 'openai/gpt-5.6-sol', label: 'OpenAI · GPT-5.6 Sol' },
  { id: 'openai/gpt-6-sol', label: 'OpenAI · GPT-6 Sol' },
  { id: 'anthropic/claude-opus-5.5', label: 'Anthropic · Claude Opus 5.5' },
  { id: 'anthropic/claude-sonnet-5.5', label: 'Anthropic · Claude Sonnet 5.5' },
  { id: 'x-ai/grok-4.7', label: 'xAI · Grok 4.7' },
  { id: 'x-ai/grok-4.6', label: 'xAI · Grok 4.6' },
  { id: 'z-ai/glm-5.3', label: 'Z.ai · GLM 5.3' },
  { id: 'z-ai/glm-5.3-flash', label: 'Z.ai · GLM 5.3 Flash' },
];

export const DOCUMENT_AGENT_OPENROUTER_MODEL_OPTIONS: StagingModelOption[] = [
  ...SHARED_STAGING_OPENROUTER_MODEL_OPTIONS,
  { id: 'google/gemini-3.8-flash', label: 'Google · Gemini 3.8 Flash' },
];

export const CONTENT_TO_PROPOSAL_STAGING_MODEL_OPTIONS = SHARED_STAGING_OPENROUTER_MODEL_OPTIONS;
export const DOCUMENT_AGENT_STAGING_MODEL_OPTIONS = DOCUMENT_AGENT_OPENROUTER_MODEL_OPTIONS;
export const STAGING_OPENROUTER_MODEL_OPTIONS = DOCUMENT_AGENT_OPENROUTER_MODEL_OPTIONS;

// The handoff steps in the order they run. The content itself is written in the designer after
// the redirect, so the last step tells the user where to watch for it.
const DOCUMENT_CREATION_STEPS: {
  id: DocumentCreationStep;
  getLabel: (documentName: string) => string;
  getActiveDetail?: (documentName: string) => string;
}[] = [
  {
    id: 'plan',
    getLabel: () => 'Creating your first draft',
    getActiveDetail: (documentName) =>
      `Building a starter ${documentName} with the content you confirmed.`,
  },
  {
    id: 'theme',
    getLabel: () => 'Choosing your theme',
  },
  {
    id: 'create',
    getLabel: (documentName) => `Setting up your ${documentName}`,
  },
  {
    id: 'open',
    getLabel: () => 'Opening the editor for review and refinement',
  },
];

export const DOCUMENT_CREATION_DESCRIPTION =
  'We’re turning your plan into a first draft. You can review and refine everything in the editor once it opens.';

export const getFirstMessageSetupSteps = (
  phase: Extract<FirstMessageState, 'inferring' | 'creating'>,
): DesignStep[] => [
  {
    label: 'Understanding your request',
    state: phase === 'inferring' ? 'active' : 'done',
    detail:
      phase === 'inferring' ? 'Picking the format, tone and length from your prompt.' : undefined,
  },
  {
    label: 'Starting your conversation',
    state: phase === 'creating' ? 'active' : 'pending',
    detail: phase === 'creating' ? 'Saving your first message.' : undefined,
  },
];

export const getDocumentCreationSteps = (
  status: DocumentCreationStatus,
  documentName: string,
): DesignStep[] => {
  const activeIndex = DOCUMENT_CREATION_STEPS.findIndex((step) => step.id === status.step);
  return DOCUMENT_CREATION_STEPS.map((step, index) => {
    const label = step.getLabel(documentName);
    if (index < activeIndex) {
      return {
        label,
        state: 'done',
        detail: step.id === 'theme' ? status.themeDetail : undefined,
      };
    }
    if (index === activeIndex) {
      return { label, state: 'active', detail: step.getActiveDetail?.(documentName) };
    }
    return { label, state: 'pending' };
  });
};

export const getThemeDetail = (
  result: ThemeSelectionResponse | null,
  selectedThemeLabel?: string,
): string => {
  if (result?.source === 'auto') return `Picked ${result.label}`;
  if (result?.source === 'existing') {
    return selectedThemeLabel ? `Using ${selectedThemeLabel}` : 'Using the theme you selected';
  }
  return 'Using your default theme';
};

// Set by apps/next on the OAuth consent page when it opens this page in a new tab after Authorize.
export const AI_CLIENT_CONNECTED_QUERY_PARAM = 'connected';

export const AI_CLIENT_CONNECTED_CLIENTS = {
  chatgpt: { name: 'ChatGPT', logo: '/images/oauth-connected/chatgpt.png' },
  claude: { name: 'Claude', logo: '/images/oauth-connected/claude.png' },
} as const;

export type AiClientConnectedId = keyof typeof AI_CLIENT_CONNECTED_CLIENTS;

export const isAiClientConnectedId = (value: string | null): value is AiClientConnectedId =>
  value !== null && value in AI_CLIENT_CONNECTED_CLIENTS;
